import React, { useState } from 'react';
import FormButton from '@app/component/form/button/FormButton';
import useModal from '@app/component/overlays/modal/ModalRef';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import CompanyService from '@app/model/company/service/CompanyService';
import { CompanyQueryKeys } from '@app/model/company/service/CompanyGraphQLQueryKeys';
import TablePlaceholder from '@app/component/placeholder/TablePlaceholder';
import Message from '@app/component/message/Message';
import _isEmpty from 'lodash/isEmpty';
import CreateOrEditCompanyDepartmentModal
	from '@app/site/secured/general-settings/fragment/company-departments/CreateOrEditCompanyDepartmentModal';
import Utilities from '@util/Utilities';
import CompanyDepartment from '@app/model/company/CompanyDepartment';
import useToaster from '@app/component/overlays/toaster/ToasterHook';
import useDialogue from '@app/component/overlays/dialog/DialogueRef';
import Dialogue from '@app/component/overlays/dialog/Dialogue';


const CompanyDepartmentsSettingsPage = React.memo(() => {
	const modal = useModal();
	const [editDepartment, setEditDepartment] = useState<CompanyDepartment>();

	const openModal = (department?: CompanyDepartment) => {
		setEditDepartment(department);
		modal.open();
	};

	return <>
		<div className="mx-auto max-w-2xl space-y-16 sm:space-y-20 lg:mx-0 lg:max-w-none bg-white p-4 sm:p-10 rounded-xl">
			<div>
				<div className="sm:flex sm:items-center">
					<div className="sm:flex-auto">
						<h1 className="text-base font-semibold leading-6 text-gray-900">
							Manage User Departments
						</h1>
						<p className="text-sm leading-6 text-gray-500">
							Manage the company's departments!
						</p>
					</div>
					<div className="mt-4 sm:ml-16 sm:mt-0 sm:flex-none">
						<FormButton state="primary" onClick={() => openModal()}>Add New Department</FormButton>
					</div>
				</div>
				<fieldset className="mt-3 pt-6 border-t border-gray-200">
					<CompanyDepartmentsTable onModalOpen={openModal} />
				</fieldset>
			</div>
		</div>
		<CreateOrEditCompanyDepartmentModal ref={modal.ref} department={editDepartment} />
	</>;
});

const CompanyDepartmentsTable: React.FunctionComponent<{
	onModalOpen: (department?: CompanyDepartment) => any
}> = React.memo(({ onModalOpen }) => {
	const toaster = useToaster();
	const queryClient = useQueryClient();

	const dialog = useDialogue<CompanyDepartment>();

	const removeCompanyDepartmentMutation = useMutation({
		mutationFn: CompanyService.removeCompanyDepartment,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: CompanyQueryKeys.departments() });
			toaster.success('Company Department', 'Department removed successfully');
		},
	});

	const onRemove = (department: CompanyDepartment) => {
		removeCompanyDepartmentMutation.mutate(department.id);
	};

	const { data, isSuccess, isLoading } = useQuery({
		queryFn: CompanyService.findCompanyDepartments,
		queryKey: CompanyQueryKeys.departments(),
	});

	if (isLoading)
		return <TablePlaceholder />;
	else if (isSuccess) {
		if (!_isEmpty(data))
			return <>
				<table className="min-w-full divide-y divide-gray-300">
					<thead>
					<tr>
						<th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-0">
							Name
						</th>
						<th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
							Creation Date
						</th>
						<th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-0">
							<span className="sr-only">Edit</span>
						</th>
					</tr>
					</thead>
					<tbody className="divide-y divide-gray-200 bg-white">
					{data.map(department => <tr key={department.id}>
						<td className="whitespace-nowrap px-3 py-5 text-sm ">
							{department.name}
						</td>
						<td className="whitespace-nowrap px-3 py-5 text-sm ">
							{Utilities.formatDateString(department.creationDate, 'dd/MM/yyyy HH:mm')}
						</td>
						<td className="whitespace-nowrap px-3 py-5 text-sm ">
							<FormButton state="text" isLink onClick={() => onModalOpen(department)}>Edit</FormButton>
							<span>|</span>
							<FormButton state="text" extraClassName="text-red-500" isLink
													onClick={() => dialog.open(department)}>Delete</FormButton>
						</td>
					</tr>)}
					</tbody>
				</table>
				<Dialogue ref={dialog.ref} title="Company Department" onConfirm={() => onRemove(dialog.getValue()!)}>
					Are you sure you want to delete this department? (All department users will by unassigned)
				</Dialogue>
			</>;
		else
			return <Message state="warning">
				<p className="flex">
					<span>There are no Departments defined!. Click</span>
					<a className="px-1 underline cursor-pointer" onClick={() => onModalOpen()}>here</a>
					<span>to add one</span>
				</p>
			</Message>;
	}

	return <>

	</>;
});

export default CompanyDepartmentsSettingsPage;